/**
 * `TourWelcome` — the tour's one-time offer (spec §2.6: "first run
 * auto-offers once … Start the tour / Not now"). A small centred card over a
 * light dim; the dim is click-through so the product stays usable
 * underneath, and the answer is always explicit — an accidental click on
 * the page must not count as "Not now", which is permanent.
 *
 * The sidebar's "Guide" row is a plain nav item (see `screens/Guide.tsx`),
 * not rendered from here.
 */
import { Compass } from "lucide-react";
import { useEffect, useRef, type CSSProperties } from "react";
import { useIsMobile } from "../../lib/useIsMobile";
import { isTypingTarget } from "../../lib/tour/typingTarget";

const PRIMARY: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  borderRadius: 12,
  border: "none",
  fontWeight: 800,
  fontSize: 13.5,
  fontFamily: "inherit",
  color: "#000",
  background: "linear-gradient(135deg, var(--ql-yellow), #FFA500)",
  boxShadow: "0 4px 16px rgba(255,213,0,.30)",
  cursor: "pointer",
};

const GHOST: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "10px 16px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "transparent",
  color: "var(--ql-fg-muted)",
  fontWeight: 600,
  fontSize: 13.5,
  fontFamily: "inherit",
  cursor: "pointer",
};

export function TourWelcome({ onStart, onDismiss }: { onStart: () => void; onDismiss: () => void }): React.JSX.Element {
  const isMobile = useIsMobile();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, []);

  // Esc = Not now (the same permanence as the button — the user reached for
  // "close", and a closed offer that comes back is a nag). Topmost layer,
  // so it owns the key (see TourProvider) — except from a text field, whose
  // own Escape must never become a permanent "Not now".
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape" || isTypingTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onDismiss]);

  return (
    <>
      <div aria-hidden className="ql-scrim-in" style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,.35)", pointerEvents: "none" }} />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby="ql-tour-welcome-title"
        tabIndex={-1}
        className="ql-tour-card"
        style={{
          position: "fixed",
          zIndex: 1001,
          left: "50%",
          top: isMobile ? "auto" : "50%",
          bottom: isMobile ? 0 : "auto",
          transform: isMobile ? "translateX(-50%)" : "translate(-50%, -50%)",
          width: isMobile ? "100%" : "min(360px, calc(100vw - 24px))",
          padding: isMobile ? "20px 20px calc(20px + env(safe-area-inset-bottom))" : 22,
          borderRadius: isMobile ? "16px 16px 0 0" : 14,
          border: "1px solid var(--ql-border-strong)",
          background: "var(--ql-bg)",
          boxShadow: "0 18px 50px rgba(0,0,0,.55)",
          outline: "none",
          animation: "ql-pop-in .22s var(--ql-ease-soft)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ width: 30, height: 30, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(255,213,0,.14)", color: "var(--ql-yellow)", flexShrink: 0 }}>
            <Compass size={16} strokeWidth={2} />
          </span>
          <div id="ql-tour-welcome-title" style={{ fontSize: 15, fontWeight: 700, color: "var(--ql-fg)", letterSpacing: "-0.01em" }}>
            Want a 2-minute walk-through?
          </div>
        </div>
        <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.55, color: "var(--ql-fg-muted)" }}>
          We&rsquo;ll build one video together — where to start, what to watch, and how to change anything.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" onClick={onDismiss} style={GHOST}>Not now</button>
          <button type="button" onClick={onStart} style={PRIMARY}>
            Start the tour
          </button>
        </div>
      </div>
    </>
  );
}
